import React, { FC, useEffect, useState } from 'react';
import { Direction } from '../../constants/Direction';
import { useTable } from '../../contexts/table/TableContext';
import { TableColumn } from '../../models/table/TableColumn';
import { TableData } from "../../models/table/TableData";
import OutsideClick from '../outside-click/outside-click';
import TableBody from './table-body/table-body';
import TableHead from './table-head/table-head';
import TableMenu from './table-menu/table-menu';

type Props = {
    className?: string,
    data: TableData[],
    columns: TableColumn[],
    selectRows?: boolean,
    customise?: boolean,
    onSelectionUpdated?: (id: string[]) => void,
    onSort?: (id: number, direction: string) => void,
    selectedIds?: string[],
    onRowClicked?: (rowData: any) => void,
    customHighlightRow?: any
    tableId?: string
    customRowColourWithRange?: any
    rowPath?: string,
    isStriped?: boolean
    cyTableName?: string
}

const Table: FC<Props> = ({
    rowPath,
    className = '',
    data = [],
    columns = [],
    selectRows = false,
    customise = false,
    onSelectionUpdated = ids => { },
    selectedIds = [],
    onSort = id => { },
    onRowClicked,
    customHighlightRow,
    tableId,
    cyTableName,
    customRowColourWithRange,
    isStriped
     }) => {

    // --> start context
    const tableContext = useTable();

    // The plan was a reference object, but it seems we need to reassign completely to correctly rerender the component.
    useEffect(() => {
        if (tableContext.tableDescriptor) {
            const newColumnDefinitions = tableContext.tableDescriptor;
            setColumnDefinitions(newColumnDefinitions);
        }
    }, [tableContext.isInUse && tableContext.tableDescriptor])

    const [showMenu, setShowMenu] = useState(false);
    const [allSelected, setAllSelected] = useState(false);
    const [rowData, setRowData] = useState<any[]>(
        new Array(0));
    const [columnDefinitions, setColumnDefinitions] = useState<TableColumn[]>(
        new Array(0));

    const handleSelect = (rowIndex) => {
        onSelectionUpdated([rowData[rowIndex].data.id]);
    };

    const handleCustomiseSubmit = (newColumns) => {
        setShowMenu(false);
        tableContext.updateTableDescriptor(newColumns);
    };

    useEffect(() => {
        if (data) {
            const newData = data.map(item => ({ data: { ...item }, selected: [...selectedIds, ...tableContext.alwaysSelectedAndDisabled!].indexOf('' + item.id) === -1 ? false : true }));
            setRowData(newData);
        }
    }, [data]);

    useEffect(() => {
        if (columns) {
            const newColDefs = columns.map((item, colIndex) => ({ ...item, colIndex, sort: Direction.ascending }));
            setColumnDefinitions([...newColDefs]);
        }
    }, [columns]);


    useEffect(() => {
        setRowData(rowData => rowData.map(item => {
            return ({ ...item, selected: allSelected });
        }));
        const rowDataIdsToFlip = rowData.filter(item => {
            return ((allSelected && selectedIds.indexOf(item.data.id) < 0)
                || (!allSelected && selectedIds.indexOf(item.data.id) !== -1));
        }).map(item => item.data.id);
        onSelectionUpdated(rowDataIdsToFlip);
    }, [allSelected])

    useEffect(() => {
        const newData = rowData => rowData.map((item, i) => ({ ...item, selected: [...selectedIds, ...tableContext.alwaysSelectedAndDisabled!].indexOf(item.data.id) !== -1 }));
        setRowData(newData);
    }, [selectedIds.length])

    return (
        <div className={`w-full flex overflow-y-auto ${className} `}>
            {showMenu
                ? <OutsideClick onClick={() => setShowMenu(false)}>
                    <TableMenu cols={columnDefinitions.filter(col => !col.isFilterOnlyColumn)} onClose={() => setShowMenu(false)} onSubmit={handleCustomiseSubmit} />
                </OutsideClick>
                : null
            }
            <table className="w-full" id={tableId ?? 'tableId'} data-cy={`${cyTableName}Table`}>
                <TableHead
                    columnDefinitions={columnDefinitions}
                    onSort={onSort}
                    selectAll={selectRows}
                    onSelectAll={() => setAllSelected(!allSelected)}
                    allSelected={allSelected}
                    customise={customise}
                    onCustomise={() => setShowMenu(true)} />
                <TableBody
                    columnDefinitions={columnDefinitions}
                    rows={rowData}
                    onSelect={handleSelect}
                    selectRows={selectRows}
                    onRowClicked={onRowClicked}
                    customHighlightRow={customHighlightRow}
                    tableId={tableId}
                    customRowColourWithRange={customRowColourWithRange}
                    rowPath={rowPath}
                    isStriped={isStriped}
                    />
            </table>
        </div>
    );
}

export default Table;
